<template>
  <div class="menu--item" :id="'menu--item-' + item.mi_id" :data-menu-item-id="item.mi_id" :class="containerClasses"
       v-if="isScheduled || $store.state.isAdminView">
    <h3 class="menu--item_title">
      <span v-html="item.mi_title"></span>
    </h3>

    <menu-schedules v-if="$store.state.isAdminView && item.normalizedSchedules.length"
                    :normalized-schedules="item.normalizedSchedules"></menu-schedules>
    <div v-if="item.mi_description.length" class="menu--item_description" v-html="item.mi_description"></div>
    <menu-item-price v-if="item.price_display.length" :price-to-display="item.price_display"
                     :price-label="item.price_label">
      <template v-slot:after-price>
        <menu-tag v-for="tag in item.tags" :key="tag.mt_id" :tag="tag" menu-type="visual"></menu-tag>
      </template>
    </menu-item-price>
  </div>
</template>

<script lang="ts">
/**
 * Custom component for Bimini cocktail items.
 *
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 1.2.0
 * @copyright  Copyright (C) 2023-2025, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 *
 *    2023-03-27 (1.0.0) (jhoward)
 *      - <biminiFoodItem> copied to <biminiCocktailItem>.
 *
 *    2024-07-19 (1.0.1) (jhoward)
 *      - Rename .visual_menu--tag CSS rules to .menu--tag in <bimini-cocktail-item>.
 *
 *    2025-02-11 (1.1.0) (jhoward)
 *      - Add <menu-schedules> to the <bimini-cocktail-item> component if in admin view.
 *      - Add support for scheduling in the <bimini-cocktail-item> component.
 *
 *    2025-04-08 (1.2.0) (jhoward)
 *      - Add <menu-item-price> in <bimini-cocktail-item>.
 *      - Remove "visual_" prefix from class names in <bimini-cocktail-item>.
 */

import MenuTag from "../menu-tag.vue";
import {defineComponent, PropType} from "vue";
import {MenuItem} from "../defs";
import {computedIsScheduled} from "../../../helpers/menu-sections";
import MenuSchedules from "../schedule/MenuSchedules.vue";
import MenuItemPrice from "./MenuItemPrice.vue";

export default defineComponent({
  name: 'biminiCocktailItem',
  components: {
    MenuItemPrice,
    MenuSchedules,
    MenuTag
  },
  props: {
    item: {
      type: Object as PropType<MenuItem>,
      required: true
    },
  },
  computed: {
    containerClasses(): { [key: string]: boolean } {
      return {
        'menu--item-highlighted': this.item.mi_highlight === 1,
        'menu--item-no_price': !this.item.price_display.length,
        'menu--item-no_description': !this.item.mi_description.length,
      };
    },
    isScheduled: computedIsScheduled,
  }
})
</script>

<style scoped>
.menu--item {
  display: grid;
  grid-template-columns: 1fr 0.2fr;
  grid-template-rows: min-content min-content;
  grid-gap: 0.25em;
}

.menu--item {
  transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1);
}

.menu--item.menu--item-search_highlighted {
  background-color: #f7f3eb !important;
}

.menu--item-no_price {
  grid-template-columns: 1fr;
}

.menu--item-no_description {
  grid-template-rows: 1fr;
}

.menu--item_title {
  position: relative;
  font-size: 1.2em;
  font-weight: bold;
}

.visual_menu--section .menu--item_title {
  font-size: 1em;
}

.textual_menu--section .menu--item_price {
  font-size: 1.2em;
}

.menu--item_description {
  grid-row: 2;
  font-weight: normal;
  margin-top: -0.3em;
}

.visual_menu--section .menu--item_description {
  font-size: 0.8em;
}

.menu--item:deep(.menu--tag) {
  position: absolute;
  left: 100%;
  top: 0.1em;
  height: 1.2em;
  width: 1.2em;
  line-height: 1.2em;
}

.menu--item-highlighted {
  background-color: #f7f3eb;
}

.menu--item:deep(.menu--item_price) {
  position: relative;
  text-align: right;
  grid-row: 1 / 3;
  grid-column: 2;
}
</style>